import * as React from 'react';
import { useTranslation } from 'react-i18next';

import ReactECharts from "echarts-for-react";
import { transpose } from "underscore";
import { isoTimeToLocalString } from "../pages/Trends/trendUtil";
import { useMemo } from 'react';
import deepMerge from '../util/deepMerge';



const calculateAverage = (arrays) => {
  if (arrays.length === 0)
    return null;

  const result = Array(arrays[0].length).fill(null);

  arrays.forEach(array => {
    array.forEach((value, index) => {
      if (value !== null)
        result[index] += value / arrays.length;
    });
  });

  return result;

}

function InputLineChart(props) {

  const MAX = 10;

  const { data, timeAggType, graphType, timestamps, localTimeZone, optionsOverrides } = props;
  const { t } = useTranslation();

  let trends = data ?? [];

  let consolidatedTrends = [];

  // workaround to only select a MAX number of curves and create a fictitious "other" curve
  if (["TEMPERATURE", "SET_POINT"].includes(graphType)) {

    consolidatedTrends = trends.slice(0, MAX);

    if (trends.length > MAX) {

      const otherTrends = trends.slice(MAX, trends.length);

      const average = calculateAverage(otherTrends.map((t) => t.values));

      consolidatedTrends.push(
        {
          metadata: {
            key: graphType,
            other: true
          },
          values: average
        }
      )
    }

  } else {
    consolidatedTrends = trends;
  }



  const dimensions = ["timestamps"]
  consolidatedTrends.forEach((item, i) => {
    dimensions.push(i.toString())
  });

  const matrix = [["timestamp", ...timestamps]];
  consolidatedTrends.forEach((item, i) => {
    matrix.push([i.toString(), ...item.values])
  });

  const source = transpose(matrix);

  let series = [];

  consolidatedTrends.forEach((trend, i) => {

    const curve = trend.metadata;

    const name = curve.other ? `${t("Other")}` : `${curve.system} - ${curve.location} - ${curve.name}`

    let s = {
      name: name,
      type: 'line',
      symbol: 'none',
      lineStyle: {
        width: 3
      },
      encode: {
        x: 'timestamp',
        y: i.toString()
      }
    }

    series.push(s)

  })

  let options = {
    dataset: {
      source: source,
      dimensions: dimensions,
    },
    tooltip: {
      confine: true,
      trigger: "axis",
      valueFormatter: (value) =>
        typeof value == "number" ? value.toLocaleString(navigator.language, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }) : value,
      axisPointer: {
        label: {
          formatter: (params) => isoTimeToLocalString(params.value, timeAggType, localTimeZone)
        }
      }
    },
    grid: {
      top: '4%',
      left: '4%',
      containLabel: true,
      right: '5%',
      bottom: 30
    },
    legend: {
      show: true,
      bottom: 0,
      left: "center",
      symbol: 'none',
      type: 'scroll',
      width: '80%',
      height: 200,
      itemGap: 20,
    },
    xAxis: {
      type: 'time',
      silent: false,
    },
    yAxis: {
      min: (value) => {
        return value.min
      },
      minInterval: graphType in ["OCCUPANCY_COUNT", "PEOPLE_IN", "PEOPLE_OUT"] ? 1 : null,
      axisLabel: {
        formatter: function (value) {
          return Math.ceil(value)
        }
      },
    },
    series: series,
    textStyle: {
      fontFamily: "Roboto, Helvetica, Arial, sans-serif"
    },
  };

  const mergedOptions = useMemo(() => {
    if (optionsOverrides) {
      return deepMerge(options, optionsOverrides);
    }
    return options;
  }, [options, optionsOverrides]);

  if (series.length === 1) {
    mergedOptions.legend = {
      show: false,
      top: '10%'
    }
    mergedOptions.grid.bottom = '0%'
  }


  return (
    <ReactECharts option={mergedOptions} notMerge={true} lazyUpdate={false} />
  )

}

export default InputLineChart;
